import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import {
  MatTableModule,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { RouterModule, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SurgicalTreatmentReportRecord } from '../../surgical-treatments-report.model';
import { GeneralCommonsModule } from '@shared/modules/general-commons/general-commons.module';
import { ReportFiltersModel, ReportType } from '@src/app/features/reports/components/report-filters/models/report-filters.model';
import { SimpleReportCellComponentModule } from '@src/app/features/reports/components/simple-report-cell/simple-report-cell.component';
import { TreatmentViolationComponentModule } from '@src/app/features/reports/components/treatment-violation/treatment-violations.component';
import { intervention, surgicalRoute } from '@utils/routing.utils';
import { BaseReportTableComponent } from '../../../../components/base-report-table/base-report-table.component';
import { PatientLinkComponentModule, PatientLinkComponent } from '../../../../components/patient-link/patient-link.component';
import { TreatmentDateComponent } from '../../../../components/treatment-date/treatment-date.component';
import { SimpleReportCellComponent } from '../../../../components/simple-report-cell/simple-report-cell.component';
import { TreatmentStatusIndicatorComponent } from '@shared/components/treatment-status-indicator/treatment-status-indicator.component';
import { TreatmentViolationsComponent } from '../../../../components/treatment-violation/treatment-violations.component';
import { ImageThumbnailComponent } from '@shared/components/image-thumbnail/image-thumbnail.component';
import { EmptyTableStateComponent } from '@shared/components/empty-table-state/empty-table-state.component';
import { LocalNamePipe } from '@shared/pipes/local-name.pipe';

@Component({
  selector: 'stx-surgical-treatment-report-table',
  templateUrl: './surgical-treatment-report-table.component.html',
  imports: [
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    SimpleReportCellComponent,
    PatientLinkComponent,
    TreatmentStatusIndicatorComponent,
    RouterLink,
    TreatmentDateComponent,
    TreatmentViolationsComponent,
    ImageThumbnailComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    EmptyTableStateComponent,
    TranslateModule,
    LocalNamePipe
  ]
})
export class SurgicalTreatmentReportTableComponent
  extends BaseReportTableComponent<SurgicalTreatmentReportRecord>
  implements OnInit, OnDestroy
{
  readonly reportType: ReportType = 'SURGICAL_TREATMENTS';

  readonly surgeryInterventionLink = `/${surgicalRoute}/${intervention}/`;

  @Input() reportConfig: ReportFiltersModel;

  displayedColumns = ['treatmentCenter', 'patient', 'operations', 'surgeon', 'anesthesiologist'];

  get reportedComplications(): boolean {
    return !!this.reportConfig.get('reportedComplications')?.reportedComplications[0];
  }

  get referringOrganization(): boolean {
    return !!this.reportConfig.get('referringOrganization')?.referringOrganization[0];
  }

  get includePhotos(): boolean {
    return !!this.reportConfig.get('includePhotos')?.includePhotos[0];
  }

  get qaStages(): boolean {
    return (
      !!this.reportConfig.get('qaSurgicalInterventions')?.qaSurgicalInterventions[0] ||
      !!this.reportConfig.get('qaPreRanks')?.qaPreRanks[0] ||
      !!this.reportConfig.get('qaPostRanks')?.qaPostRanks[0] ||
      !!this.reportConfig.get('qaSurgeryStatuses')?.qaSurgeryStatuses[0]
    );
  }

  get qaNotes(): boolean {
    return this.reportConfig.get('qaSurgeryStatuses')?.qaSurgeryStatuses.includes('flagged');
  }

  get treatmentViolations(): boolean {
    return !!this.reportConfig.get('treatmentNotCompliant')?.treatmentNotCompliant[0];
  }

  updateDisplayedColumns(): void {
    this.displayedColumns = this.reportTableDataService.updateColumnByName(
      this.reportedComplications,
      'complications',
      this.displayedColumns
    );
    this.displayedColumns = this.reportTableDataService.updateColumnByName(this.includePhotos, 'photos', this.displayedColumns);
    this.displayedColumns = this.reportTableDataService.updateColumnByName(this.qaStages, 'qaStages', this.displayedColumns);
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.onInit();
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }
}

@NgModule({
  imports: [
    MatTableModule,
    MatSortModule,
    TranslateModule,
    CommonModule,
    MatIconModule,
    RouterModule,
    MatPaginatorModule,
    PatientLinkComponentModule,
    TreatmentViolationComponentModule,
    SimpleReportCellComponentModule,
    GeneralCommonsModule,
    SurgicalTreatmentReportTableComponent
  ],
  exports: [SurgicalTreatmentReportTableComponent]
})
export class SurgicalTreatmentReportTableComponentModule {}
